/**
 * Event creation/configuration provenance — READ ONLY.
 *
 * Exists to answer three audit questions on the event page:
 *   1. When was this event created?      -> events.created_at        (NOT NULL)
 *   2. Which user created it?            -> events.created_by        (nullable)
 *   3. Which user configured it?         -> SEE "CONFIGURED" BELOW.
 *
 * ============================ "CONFIGURED" ============================
 * There is NO recorded actor for configuring an event. Verified against the
 * live QA schema (2026-09-09):
 *
 *   - `events.setup_incomplete` is the flag that means "still being set up".
 *     An event is created with setup_incomplete=true by EventCreateWizard and
 *     the operator later clears it with the "Mark as set up" button, which is
 *     a plain `updateEvent(id, { setup_incomplete: false })`
 *     (EventDetail.tsx). That UPDATE records NO actor and NO timestamp.
 *   - `events` has NO setup_completed_by / setup_completed_at column
 *     (information_schema.columns: the only `setup_%` column is
 *     `setup_incomplete`).
 *   - No trigger on `events` logs the transition (15 triggers enumerated from
 *     pg_trigger; none writes an audit row for setup_incomplete).
 *   - No audit/ledger table records it (38 *_audit / *_log / *_history /
 *     *_ledger tables enumerated; none covers event configuration).
 *   - The only DB function referencing setup_incomplete is
 *     `get_events_for_organizations`, which merely FILTERS on it.
 *
 * So we do NOT invent a "Configured by" attribution. Reporting a confident
 * wrong name is far worse than reporting "not recorded" — this surface is for
 * internal audit.
 *
 * What IS recorded, and is shown as its own separately-labelled fact, is the
 * Event-in-a-Box launch commit: `event_in_a_box_launches.created_by` (NOT NULL,
 * FK -> profiles) + `committed_at`, for rows with status='committed'. That is
 * genuinely "who committed the EIB launch", which is a configuration act for
 * EIB events only (16 of 131 QA events / 38 committed launches in PROD). It is
 * labelled as exactly that, never as "configured the event".
 *
 * ============================ ACTOR RESOLUTION ============================
 * `profiles` RLS only lets you SELECT your OWN row unless you are
 * admin/superadmin/it (`is_user_administrator()`). Proven empirically against
 * QA: an admin sees 75 profile rows, a manager sees 2 (own + one content
 * creator). So a name will NOT resolve for most roles.
 *
 * We deliberately do NOT add a SECURITY DEFINER RPC to resolve names, because
 * `events` is readable by EVERY authenticated user ("All authenticated users
 * can view events"), including the 40 `partner` accounts. Such an RPC would
 * let any partner enumerate internal staff names — a real widening of access.
 * Instead we degrade honestly (see ProvenanceActor.kind === "restricted").
 *
 * Showing the raw actor UUID widens nothing: `getEventById` already does
 * `select("*")`, so `created_by` is ALREADY in the payload of every
 * authenticated viewer of this page.
 *
 * Because BOTH actor FKs forbid dangling ids -- `events_created_by_fkey` is
 * ON DELETE SET NULL and `event_in_a_box_launches_created_by_fkey` is NO ACTION
 * -- an id that is present but whose profile row we cannot see means RLS hid
 * it, NOT that the user was deleted. That is why "restricted" is a sound
 * reading rather than a guess.
 */

import { supabase } from "./supabase";

export type UUID = string;

/**
 * All-zero UUID. Reserved sentinel for "written by an automated/service-role
 * process rather than a person".
 *
 * NOTE (verified 2026-09-09): no code path currently writes this, and no
 * service/system account exists in `profiles` (searched by email and
 * full_name for system/service/noreply/automation, plus the all-zero id:
 * zero rows in QA). This branch exists so that IF a service-role write ever
 * lands here it renders as an automated action instead of a bare UUID or a
 * blank, which is what the audit surface must never show.
 */
export const SERVICE_ACTOR_ID = "00000000-0000-0000-0000-000000000000";

/**
 * Roles permitted to SELECT event_in_a_box_launches, mirroring RLS policy
 * `eib_launches_read` (verified live 2026-09-09). Used ONLY to tell
 * "there is no committed launch" apart from "you are not allowed to see
 * whether there is one" — conflating those would be a lie on an audit
 * surface. Display-only; the database remains the enforcement point.
 */
const EIB_LAUNCH_READER_ROLES = [
  "superadmin",
  "admin",
  "manager",
  "regional_manager",
  "territory_manager",
  "staff",
];

export function canReadEibLaunches(role: string | null | undefined): boolean {
  return !!role && EIB_LAUNCH_READER_ROLES.includes(role);
}

/** How an actor resolved. Every branch is a distinct, defensible truth. */
export type ProvenanceActor =
  /** Resolved to a real person. */
  | { kind: "user"; userId: UUID; name: string | null; email: string | null }
  /** An automated/service-role process, not a person. */
  | { kind: "service"; userId: UUID }
  /** An id IS recorded, but this viewer may not read that profile row. */
  | { kind: "restricted"; userId: UUID }
  /** No actor was ever recorded for this action. */
  | { kind: "unrecorded" };

export interface EibLaunchCommit {
  actor: ProvenanceActor;
  committedAt: string | null;
}

export interface EventProvenance {
  createdAt: string | null;
  createdBy: ProvenanceActor;
  /** events.updated_at — an instant, but the system records no updated_by. */
  updatedAt: string | null;
  setupIncomplete: boolean | null;
  /**
   * Latest committed EIB launch, or null when there is none.
   * `eibLaunchReadable` says whether null is trustworthy.
   */
  eibLaunch: EibLaunchCommit | null;
  eibLaunchReadable: boolean;
}

interface ProfileRow {
  id: UUID;
  full_name: string | null;
  email: string | null;
}

/**
 * Turn a raw actor id into a display-safe, honestly-labelled actor.
 * `profileById` holds only the profile rows RLS actually returned.
 */
export function resolveActor(
  userId: UUID | null | undefined,
  profileById: Map<UUID, ProfileRow>,
): ProvenanceActor {
  if (!userId) {
    return { kind: "unrecorded" };
  }
  if (userId === SERVICE_ACTOR_ID) {
    return { kind: "service", userId };
  }
  const profile = profileById.get(userId);
  if (!profile) {
    // FK integrity (see file header) rules out a dangling id, so this is RLS.
    return { kind: "restricted", userId };
  }
  return {
    kind: "user",
    userId,
    name: profile.full_name,
    email: profile.email,
  };
}

/**
 * Read creation/configuration provenance for one event.
 *
 * Strictly read-only: three SELECTs, no writes, no RPC, no schema dependency
 * beyond columns verified to exist in BOTH QA and PROD.
 *
 * @param viewerRole the current user's `profiles.role`, used only to
 *   distinguish "no committed launch" from "not permitted to look".
 */
export async function getEventProvenance(
  eventId: UUID,
  viewerRole: string | null | undefined,
): Promise<EventProvenance> {
  // 1. The event row itself. Only the four columns this surface needs.
  const { data: eventRow, error: eventError } = await supabase
    .from("events")
    .select("created_at, created_by, updated_at, setup_incomplete")
    .eq("id", eventId)
    .single();

  if (eventError) {
    throw eventError;
  }

  const eibReadable = canReadEibLaunches(viewerRole);

  // 2. Latest COMMITTED EIB launch. status='committed' is the only status
  //    with a non-null committed_at (verified: 18 committed rows all have it,
  //    12 failed rows all have NULL).
  let launchRow: {
    created_by: UUID | null;
    committed_at: string | null;
  } | null = null;
  if (eibReadable) {
    const { data, error } = await supabase
      .from("event_in_a_box_launches")
      .select("created_by, committed_at")
      .eq("event_id", eventId)
      .eq("status", "committed")
      // Who set the event up = the latest wizard launch, not a later radius expansion.
      .eq("launch_kind", "original")
      .order("committed_at", { ascending: false })
      .limit(1);
    // A read failure must not take down the whole panel; treat it as
    // "not readable" rather than silently claiming there is no launch.
    if (error) {
      launchRow = null;
    } else {
      launchRow = data && data.length > 0 ? data[0] : null;
    }
  }

  // 3. Resolve both actor ids in ONE profiles query. RLS decides what comes
  //    back; anything withheld becomes kind:"restricted" rather than blank.
  const actorIds = Array.from(
    new Set(
      [eventRow?.created_by, launchRow?.created_by].filter(
        (v): v is UUID => !!v && v !== SERVICE_ACTOR_ID,
      ),
    ),
  );

  const profileById = new Map<UUID, ProfileRow>();
  if (actorIds.length > 0) {
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, full_name, email")
      .in("id", actorIds);
    for (const p of (profiles ?? []) as ProfileRow[]) {
      profileById.set(p.id, p);
    }
  }

  return {
    createdAt: eventRow?.created_at ?? null,
    createdBy: resolveActor(eventRow?.created_by, profileById),
    updatedAt: eventRow?.updated_at ?? null,
    setupIncomplete: eventRow?.setup_incomplete ?? null,
    eibLaunch: launchRow
      ? {
          actor: resolveActor(launchRow.created_by, profileById),
          committedAt: launchRow.committed_at,
        }
      : null,
    eibLaunchReadable: eibReadable,
  };
}
